import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import {
  api,
  requeteApi,
  requeteMultipart,
  requeteTelechargement,
  type AppelApi,
} from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import { estApiError } from './errors.js';
import i18n from '../i18n.js';
import type { Pagination } from './types.js';

export interface PresetCsv {
  colonnes: {
    dateOperation: string | number;
    libelle: string | number;
    montant?: string | number;
    debit?: string | number;
    credit?: string | number;
    dateValeur?: string | number;
    reference?: string | number;
  };
  decimal: ',' | '.';
  formatDate: 'JJ/MM/AAAA' | 'AAAA-MM-JJ' | 'JJ-MM-AAAA';
  separateur?: ';' | ',' | '\t' | '|';
  ignorerLignes?: number;
}
export interface CorpsCompte {
  libelle: string;
  iban: string;
  bic: string;
  devise: 'EUR';
  compteComptable: string;
  soldeOuvertureCentimes: number;
  soldeOuvertureLe: string;
  ferme: boolean;
  presetCsv?: PresetCsv;
}
export interface CompteBancaire extends CorpsCompte {
  id: string;
}
export interface Solde {
  montantCentimes: number;
  date: string;
}
export interface SoldeCompte {
  soldeCentimes: number;
  soldeLe: string;
  nonRapprochees: number;
  soldeBanque?: Solde;
  ecartSolde?: number;
  soldeCalculeAuReleve?: Solde;
}
export interface Synthese {
  comptes: (CompteBancaire & SoldeCompte & { dernierReleveLe: string | null })[];
  nonRapprochees: number;
  nombreEcarts: number;
  prochainPassageSousSeuil: { le: string; soldeCentimes: number } | null;
}
export interface LigneBancaire {
  id: string;
  compteId: string;
  importId: string;
  dateOperation: string;
  dateValeur: string;
  montantCentimes: number;
  libelle: string;
  contrepartie: string;
  reference: string;
  remise: string;
  rapprochementId: string | null;
}
export type TypePiece = 'reglement' | 'remboursement' | 'reversement' | 'depense';
export interface Piece {
  type: TypePiece;
  id: string;
  montantCentimes: number;
  date: string;
  echeanceLe?: string;
  numero: string;
  contactNom: string;
  version?: number;
}
export type NatureEcart = 'frais_bancaires' | 'commission_paiement' | 'arrondi' | 'autre';
export type NatureClassement =
  'virement_interne' | 'apport' | 'emprunt' | 'impot' | 'salaire' | 'autre';
export interface CorpsRapprochement {
  ligneId: string;
  pieces: { type: TypePiece; id: string }[];
  ecart?: { nature: NatureEcart; motif: string };
}
export interface Rapprochement {
  id: string;
  ligneId: string;
  etat: 'actif' | 'defait' | 'orphelin';
  pieces: { type: TypePiece; id: string; montantCentimes: number }[];
  ecart?: { nature: NatureEcart; motif: string; montantCentimes: number };
  classement?: { nature: NatureClassement; motif: string };
}
export interface Ecarts {
  au: string;
  nombre: number;
  lignesAnciennes: LigneBancaire[];
  piecesSansLigne: Piece[];
  encaissementsNonEnregistres: {
    factureId: string;
    numero: string;
    ligneId: string;
    montantCentimes: number;
    dateOperation: string;
    reference: string;
  }[];
  orphelins: Rapprochement[];
  avecEcart: Rapprochement[];
  etats: (SoldeCompte & {
    compteId: string;
    compte: string;
    lignesNonRapprochees: LigneBancaire[];
    nonRapprocheCentimes: number;
  })[];
}
export const CATEGORIES_DEPENSE = [
  'loyer',
  'salaires',
  'transport',
  'production',
  'assurance',
  'foire',
  'honoraires',
  'impots_taxes',
  'banque',
  'autre',
] as const;
export type CategorieDepense = (typeof CATEGORIES_DEPENSE)[number];
export interface CorpsDepense {
  libelle: string;
  fournisseurNom: string;
  fournisseurContactId?: string;
  categorie: CategorieDepense;
  montantCentimes: number;
  date: string;
  echeanceLe: string;
  note: string;
  piece?: { type: 'document' | 'media'; id: string };
}
export interface Depense extends CorpsDepense {
  id: string;
  version: number;
  etat: 'a_payer' | 'payee';
}
export interface CorpsCharge {
  libelle: string;
  categorie: CategorieDepense;
  montantCentimes: number;
  periodicite: 'mensuelle' | 'trimestrielle' | 'annuelle';
  premierMois: string;
  dernierMois?: string;
  jourDuMois: number;
  active: boolean;
}
export interface Charge extends CorpsCharge {
  id: string;
}
export interface ReglagesTresorerie {
  delaiEncaissementPipelineJours: number;
  delaiReversementJours: number;
  echeanceParDefautJours: number;
  seuilAlerteCentimes: number;
  ecartMaxPdb: number;
  ecartMaxCentimes: number;
  joursAvantEcart: number;
}
export type Horizon = '13s' | '6m' | '12m';
export interface Prevision {
  depart: { soldeCentimes: number; le: string };
  seuilCentimes: number;
  devise: 'EUR';
  sourcesExclues: string[];
  alertes: { le: string; soldeCentimes: number }[];
  pas: {
    debut: string;
    fin: string;
    soldeDebut: number;
    soldeFin: number;
    sousSeuil: boolean;
    encaissements: { factures: number; enRetard: number; pipeline: number };
    decaissements: {
      depenses: number;
      charges: number;
      reversements: number;
      remboursements: number;
    };
  }[];
}
export interface NombresImport {
  lues: number;
  nouvelles: number;
  connues: number;
}
export interface ImportBancaire {
  id: string;
  compteId: string;
  source: 'fichier';
  format: 'camt053' | 'ofx' | 'csv';
  nomFichier: string;
  empreinteFichier: string;
  taille: number;
  periode: { du: string; au: string };
  soldeOuverture?: Solde;
  soldeCloture?: Solde;
  nombres: NombresImport;
  createdAt: string;
}
export interface ApercuImport {
  format: 'camt053' | 'ofx' | 'csv';
  devise: 'EUR';
  lues: number;
  lignes: Omit<LigneBancaire, 'id' | 'compteId' | 'importId' | 'rapprochementId'>[];
}
export interface ResultatImport {
  import: ImportBancaire;
  dejaImporte: boolean;
  nombres: NombresImport;
}
export interface FiltresLignes {
  compteId?: string;
  etat?: 'a_traiter' | 'rapprochee';
  du?: string;
  au?: string;
  page?: number;
  parPage?: number;
}
export interface FiltresDepenses {
  etat?: 'a_payer' | 'payee';
  categorie?: CategorieDepense;
  du?: string;
  au?: string;
}
export interface Depot {
  compteId: string;
  fichier: File;
  presetCsv?: PresetCsv;
}
const base = (g: string) => `/g/${encodeURIComponent(g)}/tresorerie`;
function parametres(f: object) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') q.set(k, String(v));
  return `?${q}`;
}
function depot(g: string, v: Depot, apercu: boolean, a?: AppelApi) {
  const donnees = new FormData();
  donnees.append('fichier', v.fichier);
  if (v.presetCsv) donnees.append('presetCsv', JSON.stringify(v.presetCsv));
  return {
    chemin: `${base(g)}/comptes/${encodeURIComponent(v.compteId)}/imports${apercu ? '?apercu=1' : ''}`,
    donnees,
    options: { galerie: g, ...a },
  };
}
export const tresorerieApi = {
  synthese: (g: string, a?: AppelApi) => api.get<Synthese>(base(g), { galerie: g, ...a }),
  comptes: (g: string, a?: AppelApi) =>
    api.get<{ comptes: CompteBancaire[] }>(`${base(g)}/comptes`, { galerie: g, ...a }),
  creerCompte: (g: string, c: CorpsCompte, a?: AppelApi) =>
    api.post<{ compte: CompteBancaire }>(`${base(g)}/comptes`, c, { galerie: g, ...a }),
  modifierCompte: (
    g: string,
    id: string,
    c: Partial<Omit<CorpsCompte, 'iban' | 'devise'>>,
    a?: AppelApi,
  ) => api.patch<{ compte: CompteBancaire }>(`${base(g)}/comptes/${id}`, c, { galerie: g, ...a }),
  imports: (g: string, f: Pick<FiltresLignes, 'compteId' | 'page' | 'parPage'>, a?: AppelApi) =>
    api.get<{ imports: ImportBancaire[]; pagination: Pagination }>(
      `${base(g)}/imports${parametres(f)}`,
      { galerie: g, ...a },
    ),
  apercu: (g: string, v: Depot, a?: AppelApi) => {
    const d = depot(g, v, true, a);
    return requeteMultipart<ApercuImport>(d.chemin, d.donnees, d.options);
  },
  importer: (g: string, v: Depot, a?: AppelApi) => {
    const d = depot(g, v, false, a);
    return requeteMultipart<ResultatImport>(d.chemin, d.donnees, d.options);
  },
  lignes: (g: string, f: FiltresLignes, a?: AppelApi) =>
    api.get<{ lignes: LigneBancaire[]; pagination: Pagination }>(
      `${base(g)}/lignes${parametres(f)}`,
      { galerie: g, ...a },
    ),
  suggestions: (g: string, id: string, recherche: string, a?: AppelApi) =>
    api.get<{ suggestions: Piece[] }>(
      `${base(g)}/lignes/${id}/suggestions${parametres({ recherche })}`,
      { galerie: g, ...a },
    ),
  rapprocher: (g: string, c: CorpsRapprochement, a?: AppelApi) =>
    api.post<{ rapprochement: Rapprochement }>(`${base(g)}/rapprochements`, c, {
      galerie: g,
      ...a,
    }),
  classer: (g: string, id: string, c: { nature: NatureClassement; motif: string }, a?: AppelApi) =>
    api.post<{ rapprochement: Rapprochement }>(`${base(g)}/lignes/${id}/classer`, c, {
      galerie: g,
      ...a,
    }),
  depenseLigne: (
    g: string,
    id: string,
    c: Omit<CorpsDepense, 'montantCentimes' | 'date' | 'echeanceLe'>,
    a?: AppelApi,
  ) =>
    api.post<{ depense: Depense; rapprochement: Rapprochement }>(
      `${base(g)}/lignes/${id}/depense`,
      c,
      { galerie: g, ...a },
    ),
  defaire: (g: string, id: string, motif: string, a?: AppelApi) =>
    api.post<{ rapprochement: Rapprochement }>(
      `${base(g)}/rapprochements/${id}/defaire`,
      { motif },
      { galerie: g, ...a },
    ),
  automatique: (g: string, c: { compteId?: string }, a?: AppelApi) =>
    api.post<{ travailId: string }>(`${base(g)}/rapprocher`, c, { galerie: g, ...a }),
  ecarts: (g: string, au: string, a?: AppelApi) =>
    api.get<Ecarts>(`${base(g)}/ecarts${parametres({ au })}`, { galerie: g, ...a }),
  depenses: (g: string, f: FiltresDepenses, a?: AppelApi) =>
    api.get<{ depenses: Depense[] }>(`${base(g)}/depenses${parametres(f)}`, { galerie: g, ...a }),
  creerDepense: (g: string, c: CorpsDepense, a?: AppelApi) =>
    api.post<{ depense: Depense }>(`${base(g)}/depenses`, c, { galerie: g, ...a }),
  modifierDepense: (
    g: string,
    id: string,
    c: Partial<CorpsDepense> & { versionAttendue: number },
    a?: AppelApi,
  ) => api.patch<{ depense: Depense }>(`${base(g)}/depenses/${id}`, c, { galerie: g, ...a }),
  supprimerDepense: (g: string, id: string, versionAttendue: number, a?: AppelApi) =>
    requeteApi<void>(`${base(g)}/depenses/${id}`, {
      method: 'DELETE',
      galerie: g,
      corps: { versionAttendue },
      ...a,
    }),
  charges: (g: string, a?: AppelApi) =>
    api.get<{ charges: Charge[] }>(`${base(g)}/charges`, { galerie: g, ...a }),
  creerCharge: (g: string, c: CorpsCharge, a?: AppelApi) =>
    api.post<{ charge: Charge }>(`${base(g)}/charges`, c, { galerie: g, ...a }),
  modifierCharge: (g: string, id: string, c: Partial<CorpsCharge>, a?: AppelApi) =>
    api.patch<{ charge: Charge }>(`${base(g)}/charges/${id}`, c, { galerie: g, ...a }),
  prevision: (g: string, horizon: Horizon, a?: AppelApi) =>
    api.get<Prevision>(`${base(g)}/prevision${parametres({ horizon })}`, { galerie: g, ...a }),
  reglages: (g: string, a?: AppelApi) =>
    api.get<{ reglages: ReglagesTresorerie }>(`${base(g)}/reglages`, { galerie: g, ...a }),
  modifierReglages: (g: string, c: ReglagesTresorerie, a?: AppelApi) =>
    api.put<{ reglages: ReglagesTresorerie }>(`${base(g)}/reglages`, c, { galerie: g, ...a }),
  exporter: (g: string, f: { du: string; au: string }, a?: AppelApi) =>
    requeteTelechargement(`${base(g)}/export.csv${parametres(f)}`, { galerie: g, ...a }),
};
export function messageTresorerie(e: unknown): string {
  if (estApiError(e)) {
    const raison = (e.details as { raison?: string } | null)?.raison;
    if (raison?.startsWith('tresorerie.') && i18n.exists(`erreurs:${raison}`))
      return i18n.t(`erreurs:${raison}`);
    return e.message;
  }
  return i18n.t('commun:erreurReseau');
}
function useLecture<T>(
  g: string,
  cle: readonly unknown[],
  lire: (a: AppelApi) => Promise<T>,
  actif = true,
) {
  return useQuery({
    queryKey: cleMetier(g, 'tresorerie', ...cle),
    queryFn: (c) => lire(appelDeRequete(c)),
    enabled: actif,
  });
}
export const useSyntheseTresorerie = (g: string) =>
  useLecture(g, ['synthese'], (a) => tresorerieApi.synthese(g, a));
export const useComptesBancaires = (g: string) =>
  useLecture(g, ['comptes'], (a) => tresorerieApi.comptes(g, a));
export const useImportsBancaires = (
  g: string,
  f: Pick<FiltresLignes, 'compteId' | 'page' | 'parPage'>,
) => useLecture(g, ['imports', f], (a) => tresorerieApi.imports(g, f, a));
export const useLignesBancaires = (g: string, f: FiltresLignes) =>
  useLecture(g, ['lignes', f], (a) => tresorerieApi.lignes(g, f, a));
export const useSuggestionsBancaires = (g: string, id: string, recherche = '') =>
  useLecture(
    g,
    ['suggestions', id, recherche],
    (a) => tresorerieApi.suggestions(g, id, recherche, a),
    !!id,
  );
export const useEcartsTresorerie = (g: string, au: string) =>
  useLecture(g, ['ecarts', au], (a) => tresorerieApi.ecarts(g, au, a));
export const useDepenses = (g: string, f: FiltresDepenses = {}) =>
  useLecture(g, ['depenses', f], (a) => tresorerieApi.depenses(g, f, a));
export const useCharges = (g: string) =>
  useLecture(g, ['charges'], (a) => tresorerieApi.charges(g, a));
export const usePrevisionTresorerie = (g: string, h: Horizon) =>
  useLecture(g, ['prevision', h], (a) => tresorerieApi.prevision(g, h, a));
export const useReglagesTresorerie = (g: string) =>
  useLecture(g, ['reglages'], (a) => tresorerieApi.reglages(g, a));
function useGeste<T, R>(g: string, executer: (v: T, a: AppelApi) => Promise<R>) {
  const q = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: executer,
      onSuccess: () => {
        void q.invalidateQueries({ queryKey: cleMetier(g, 'tresorerie') });
      },
    }),
  );
}
export const useCreerCompteBancaire = (g: string) =>
  useGeste(g, (c: CorpsCompte, a) => tresorerieApi.creerCompte(g, c, a));
export const useModifierCompteBancaire = (g: string) =>
  useGeste(g, (v: { id: string; corps: Partial<Omit<CorpsCompte, 'iban' | 'devise'>> }, a) =>
    tresorerieApi.modifierCompte(g, v.id, v.corps, a),
  );
export const useApercuBancaire = (g: string) =>
  useMutation(dansLaPortee({ mutationFn: (v: Depot, a) => tresorerieApi.apercu(g, v, a) }));
export const useImporterReleve = (g: string) =>
  useGeste(g, (v: Depot, a) => tresorerieApi.importer(g, v, a));
export const useRapprocherLigne = (g: string) =>
  useGeste(g, (v: CorpsRapprochement, a) => tresorerieApi.rapprocher(g, v, a));
export const useClasserLigne = (g: string) =>
  useGeste(g, (v: { id: string; nature: NatureClassement; motif: string }, a) =>
    tresorerieApi.classer(g, v.id, { nature: v.nature, motif: v.motif }, a),
  );
export const useDepenseDepuisLigne = (g: string) =>
  useGeste(
    g,
    (v: { id: string; corps: Omit<CorpsDepense, 'montantCentimes' | 'date' | 'echeanceLe'> }, a) =>
      tresorerieApi.depenseLigne(g, v.id, v.corps, a),
  );
export const useDefaireRapprochement = (g: string) =>
  useGeste(g, (v: { id: string; motif: string }, a) => tresorerieApi.defaire(g, v.id, v.motif, a));
export const useRapprochementAutomatique = (g: string) =>
  useGeste(g, (v: { compteId?: string }, a) => tresorerieApi.automatique(g, v, a));
export const useCreerDepense = (g: string) =>
  useGeste(g, (v: CorpsDepense, a) => tresorerieApi.creerDepense(g, v, a));
export const useModifierDepense = (g: string) =>
  useGeste(g, (v: { id: string; corps: Partial<CorpsDepense> & { versionAttendue: number } }, a) =>
    tresorerieApi.modifierDepense(g, v.id, v.corps, a),
  );
export const useSupprimerDepense = (g: string) =>
  useGeste(g, (v: { id: string; versionAttendue: number }, a) =>
    tresorerieApi.supprimerDepense(g, v.id, v.versionAttendue, a),
  );
export const useCreerCharge = (g: string) =>
  useGeste(g, (v: CorpsCharge, a) => tresorerieApi.creerCharge(g, v, a));
export const useModifierCharge = (g: string) =>
  useGeste(g, (v: { id: string; corps: Partial<CorpsCharge> }, a) =>
    tresorerieApi.modifierCharge(g, v.id, v.corps, a),
  );
export const useModifierReglagesTresorerie = (g: string) =>
  useGeste(g, (v: ReglagesTresorerie, a) => tresorerieApi.modifierReglages(g, v, a));
export const useExporterTresorerie = (g: string) =>
  useMutation(
    dansLaPortee({
      mutationFn: (v: { du: string; au: string }, a) => tresorerieApi.exporter(g, v, a),
    }),
  );
